import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated, getRedirectPath, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && user) {
      const redirectPath = getRedirectPath(user.user_type);
      navigate(redirectPath);
    }
  }, [isAuthenticated, user, navigate, getRedirectPath]);

  useEffect(() => {
    const cookies = document.cookie.split(';');
    const usernameCookie = cookies.find(c => c.trim().startsWith('username='));
    if (usernameCookie) {
      const savedUsername = usernameCookie.split('=')[1];
      if (savedUsername) {
        setUsername(savedUsername);
        setRememberMe(true);
      }
    }
  }, []);

  useEffect(() => {
    const bubbleSizes = [80, 100, 120, 150, 180, 200, 250];
    const bubblePositions = [
      { top: '10%', left: '5%' },
      { top: '20%', right: '10%' },
      { top: '40%', left: '15%' },
      { top: '60%', right: '5%' },
      { top: '80%', left: '10%' },
      { top: '30%', left: '80%' },
      { top: '70%', left: '85%' },
      { top: '85%', right: '15%' }
    ];

    for (let i = 0; i < 8; i++) {
      const bubble = document.createElement('div');
      bubble.className = 'bubble';
      const size = bubbleSizes[Math.floor(Math.random() * bubbleSizes.length)];
      bubble.style.width = `${size}px`;
      bubble.style.height = `${size}px`;
      
      const position = bubblePositions[i];
      Object.assign(bubble.style, position);
      document.body.appendChild(bubble);
    }

    return () => {
      document.querySelectorAll('.bubble').forEach(bubble => bubble.remove());
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!username.trim() || !password.trim()) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    const result = await login(username.trim(), password, rememberMe);

    if (result.success) {
      const redirectPath = getRedirectPath(result.user.user_type);
      navigate(redirectPath);
    } else {
      setError(result.message || 'Invalid username or password');
      setLoading(false);
    }
  };

  const inputClass =
    'h-[52px] w-full rounded-xl border-2 border-slate-200 bg-white px-4 text-[0.95rem] leading-normal text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-brand focus:ring-4 focus:ring-brand-light disabled:cursor-not-allowed disabled:bg-slate-100';

  return (
    <div className="relative z-10 flex min-h-screen items-center justify-center overflow-x-hidden bg-white p-6 font-sans sm:p-8">
      <div className="w-full max-w-[480px] overflow-hidden rounded-3xl bg-white shadow-[0_10px_30px_rgba(0,0,0,0.1)]">
        <div className="relative z-[2] flex flex-col justify-center overflow-hidden bg-white px-8 py-10 max-sm:px-8 sm:px-10 sm:py-12">
          <img
            src="https://qure.science/images/qure.webp"
            alt="Qure"
            className="mx-auto mb-10 h-auto w-[160px] max-sm:mb-6 max-sm:w-[120px]"
            style={{
              filter:
                'invert(44%) sepia(91%) saturate(2044%) hue-rotate(347deg) brightness(99%) contrast(95%)'
            }}
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/160x60?text=Qure';
            }}
          />

          <h1 className="mb-2 break-words text-center text-[1.75rem] font-semibold leading-snug text-qure-secondary max-sm:text-2xl">
            Sign in to Qure
          </h1>
          <p className="mb-8 break-words text-center leading-normal text-gray-500">
            Welcome back! Please enter your details.
          </p>

          {error && (
            <div
              className="mb-6 flex animate-auth-slide-in items-center gap-3 rounded-xl border-0 bg-red-50 px-4 py-3 text-red-700"
              role="alert"
            >
              <i className="bi bi-exclamation-circle-fill shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form id="loginForm" onSubmit={handleSubmit}>
            <div className="relative mb-5 w-full">
              <input
                type="text"
                className={inputClass}
                id="username"
                name="username"
                placeholder="Username or Email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="relative mb-5 w-full">
              <input
                type="password"
                className={inputClass}
                id="password"
                name="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="mb-5 flex items-center gap-2">
              <input
                type="checkbox"
                className="h-[1.1rem] w-[1.1rem] cursor-pointer accent-brand"
                id="remember_me"
                name="remember_me"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loading}
              />
              <label className="cursor-pointer select-none text-[0.9rem] text-gray-600" htmlFor="remember_me">
                Keep me signed in
              </label>
            </div>

            <div className="mb-5 grid gap-3">
              <button
                type="submit"
                className="full-width-btn flex h-12 items-center justify-center gap-2 rounded-[10px] border-0 bg-brand text-[0.95rem] font-medium text-white transition hover:bg-brand-dark hover:shadow-[0_4px_12px_rgba(217,90,23,0.25)] disabled:cursor-not-allowed disabled:opacity-60"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span
                      className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent"
                      role="status"
                      aria-hidden="true"
                    />
                    Signing in...
                  </>
                ) : (
                  <>
                    <i className="bi bi-box-arrow-in-right" />
                    Sign In
                  </>
                )}
              </button>
              <Link
                to="/register"
                className="full-width-btn flex h-12 items-center justify-center gap-2 rounded-[10px] border border-gray-300 bg-transparent text-[0.95rem] font-medium text-gray-700 no-underline transition hover:bg-gray-100 hover:border-gray-400"
              >
                <i className="bi bi-person-plus" />
                Create New Account
              </Link>
            </div>

            <div className="mb-5 text-center">
              <Link
                to="/forgot-password"
                className="text-[0.9rem] text-gray-500 no-underline hover:text-brand"
              >
                Forgot your password?
              </Link>
            </div>

            <div className="grid gap-3">
              <a
                href="https://qure.science/qurexr/telepresence"
                className="full-width-btn flex h-12 items-center justify-center gap-2 rounded-[10px] border-0 bg-gradient-to-br from-[#1e88e5] to-[#1565c0] text-[0.95rem] font-medium text-white no-underline shadow-none transition hover:from-[#1565c0] hover:to-[#0d47a1] hover:shadow-[0_4px_12px_rgba(30,136,229,0.3)]"
                target="_self"
                rel="noopener noreferrer"
              >
                <i className="bi bi-camera-video" />
                Telepresence
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
